import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { RemanenteDestino } from "@/lib/portal-estructura-comisiones/types/simulator";

/**
 * Cuadre de la comisión por canal — la ecuación de cierre de un canal:
 *
 * ```
 * comisión_total = comisión_externa + Σ(comisionistas) + remanente
 * ```
 *
 * La fórmula vive en la BD (`comisiones_cuadre(p_id_proyecto)`), no aquí: la
 * comparten esta pantalla, el envío a validación y el trigger
 * `fn_comisiones_no_excede()` que rechaza dispersar de más. Tenerla en un solo
 * lugar es el punto — antes estaba duplicada entre `CommissionsTab` y una copia
 * muerta en `calculations.ts` que además usaba otra fórmula.
 *
 * Ver `Ejecuciones_manuales/20260831_comisiones_cierre_canal.md`.
 *
 * Probe graceful (patrón #6 de CLAUDE.md): mientras el DDL esté pendiente la
 * RPC no existe, el hook devuelve `null` y la pantalla cae a su cálculo local.
 */

/** PostgREST devuelve esto cuando la función todavía no existe (DDL pendiente). */
const FUNCTION_MISSING_CODES = ["PGRST202", "42883"];

/** El trigger rechazó la escritura: algún canal dispersa más que su total. */
export const SQLSTATE_EXCEDIDO = "CM001";
/** El payload de la RPC de guardado es inválido (p. ej. regla sin comisionista). */
export const SQLSTATE_PAYLOAD = "CM002";

export const isFunctionMissing = (error?: { code?: string } | null) =>
  !!error?.code && FUNCTION_MISSING_CODES.includes(error.code);

export type { RemanenteDestino } from "@/lib/portal-estructura-comisiones/types/simulator";

export const DESTINOS_REMANENTE: { valor: RemanenteDestino; etiqueta: string; ayuda: string }[] = [
  {
    valor: "utilidad_sozu",
    etiqueta: "Utilidad SOZU",
    ayuda: "El sobrante se queda en SOZU como fee de comercialización no dispersado.",
  },
  {
    valor: "utilidad_proyecto",
    etiqueta: "Utilidad del desarrollo",
    ayuda: "El sobrante regresa al desarrollo: comisión presupuestada que no se paga.",
  },
  {
    valor: "sin_asignar",
    etiqueta: "Sin asignar",
    ayuda: "Todavía no se decide a dónde va. El canal no cierra mientras siga así.",
  },
];

export type EstadoCuadre =
  /** `comision_total_pct = 0` — el canal aún no se configura. */
  | "sin_definir"
  /** Se dispersa más que el total. La BD lo rechaza; solo llega aquí si el DDL está pendiente. */
  | "excedido"
  /** Cierra exacto: no sobra nada. */
  | "completo"
  /** Sobra, y ya se declaró a dónde va. */
  | "declarado"
  /** Sobra y nadie ha dicho de quién es. */
  | "falta_declarar";

export interface CuadreCanal {
  idCanal: string;
  canal: string;
  aplica: boolean;
  comisionTotalPct: number;
  comisionExternaPct: number;
  comisionInternaPct: number;
  dispersadaPct: number;
  remanentePct: number;
  remanenteDestino: RemanenteDestino;
  comisionistas: number;
  estado: EstadoCuadre;
}

const CUADRE_KEY = "comisiones-cuadre";

/** Misma tolerancia que usa el trigger en la BD. */
export const TOLERANCIA_CUADRE = 0.0005;

/**
 * `disponible: false` = el DDL de cierre sigue pendiente, así que no hay ni RPC
 * ni columna `remanente_destino`. Se distingue de "disponible pero sin canales"
 * a propósito: en el primer caso la pantalla tiene que avisar que el destino del
 * remanente **no se va a guardar**; en el segundo, simplemente no hay nada que
 * mostrar.
 */
export interface CuadreProyecto {
  disponible: boolean;
  canales: CuadreCanal[];
}

export function useComisionesCuadre(idProyecto: number | null) {
  return useQuery<CuadreProyecto>({
    queryKey: [CUADRE_KEY, idProyecto],
    enabled: idProyecto != null,
    staleTime: 30_000,
    retry: (fallos, error: any) => !isFunctionMissing(error) && fallos < 2,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("comisiones_cuadre", {
        p_id_proyecto: idProyecto,
      });
      if (error) {
        if (isFunctionMissing(error)) return { disponible: false, canales: [] };
        throw error;
      }
      return {
        disponible: true,
        canales: ((data ?? []) as any[]).map(
          (r): CuadreCanal => ({
            idCanal: String(r.id_canal),
            canal: r.canal,
            aplica: r.aplica ?? true,
            comisionTotalPct: Number(r.comision_total_pct ?? 0),
            comisionExternaPct: Number(r.comision_externa_pct ?? 0),
            comisionInternaPct: Number(r.comision_interna_pct ?? 0),
            dispersadaPct: Number(r.dispersada_pct ?? 0),
            remanentePct: Number(r.remanente_pct ?? 0),
            remanenteDestino: (r.remanente_destino ?? "sin_asignar") as RemanenteDestino,
            comisionistas: Number(r.comisionistas ?? 0),
            estado: r.estado as EstadoCuadre,
          }),
        ),
      };
    },
  });
}

/**
 * Misma clasificación que la RPC, para cuando el DDL sigue pendiente y hay que
 * calcularla en el cliente. Es una réplica deliberada de la lógica de
 * `comisiones_cuadre()`: si una cambia, la otra tiene que cambiar igual.
 */
export function clasificarCuadre(
  comisionTotalPct: number,
  remanentePct: number,
  destino: RemanenteDestino,
  totalDefinido: boolean,
): EstadoCuadre {
  if (!totalDefinido || comisionTotalPct === 0) return "sin_definir";
  if (remanentePct < -TOLERANCIA_CUADRE) return "excedido";
  if (Math.abs(remanentePct) <= TOLERANCIA_CUADRE) return "completo";
  return destino === "sin_asignar" ? "falta_declarar" : "declarado";
}

/**
 * Qué impide mandar la estructura a Alta Dirección.
 *
 * `excedido` y `sin_definir` bloquean: la primera es una estructura imposible,
 * la segunda es una a medio capturar. `falta_declarar` no bloquea —el sobrante
 * puede ser perfectamente intencional— pero se advierte antes de enviar, que es
 * el último momento en que alguien puede notarlo.
 */
export interface GateValidacion {
  puedeEnviar: boolean;
  excedidos: CuadreCanal[];
  sinDefinir: CuadreCanal[];
  faltaDeclarar: CuadreCanal[];
}

export function evaluarGateValidacion(cuadre: CuadreCanal[] | undefined): GateValidacion {
  const aplicables = (cuadre ?? []).filter(c => c.aplica);
  const excedidos = aplicables.filter(c => c.estado === "excedido");
  const sinDefinir = aplicables.filter(c => c.estado === "sin_definir");
  const faltaDeclarar = aplicables.filter(c => c.estado === "falta_declarar");
  return {
    puedeEnviar: excedidos.length === 0 && sinDefinir.length === 0,
    excedidos,
    sinDefinir,
    faltaDeclarar,
  };
}

/**
 * Traduce el error de la BD a algo que el usuario pueda accionar.
 *
 * `CM001` ya trae en `message` el canal y los puntos de exceso —lo arma el
 * trigger—, así que se muestra tal cual en vez de reescribirlo aquí.
 */
export function mensajeErrorCuadre(error: { code?: string; message?: string; hint?: string } | null): string | null {
  if (!error) return null;
  if (error.code === SQLSTATE_EXCEDIDO || error.code === SQLSTATE_PAYLOAD) {
    return [error.message, error.hint].filter(Boolean).join(" ");
  }
  return null;
}
